<script setup lang="ts">
/**
 * Author route.
 *
 * The page whose subject is the entity in `config/identity.ts` rather than the product the home
 * route describes. It carries the `ProfilePage` the home route cannot — see the remarks on
 * `authorPage` in `config/entities.ts` — and it is where the identity signals stop being invisible:
 * structured data claiming a job title, a topic or a registry identifier that a visitor cannot find
 * on the page is an assertion, and this is the page that makes each of them checkable.
 *
 * Nothing here is typed out. Every value comes from `identityConfig`, the same object `entities.ts`
 * builds the `Person` node from, so the visible page and the JSON-LD cannot drift apart. Each
 * section is guarded on its own array for the other half of that: a fork that leaves `identifiers`
 * empty gets no identifiers section, rather than a heading over nothing.
 */

/**
 * Vue
 */
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import { ArrowUpRight, AtSign, BadgeCheck, Compass, Globe, Languages, MapPin, Sparkles } from '@lucide/vue'

/**
 * Components
 */
import AppBackdrop from '@/components/ui/AppBackdrop.vue'
import AppBadge from '@/components/ui/AppBadge.vue'
import AppButton from '@/components/ui/AppButton.vue'
import AppCard from '@/components/ui/AppCard.vue'
import AppContainer from '@/components/ui/AppContainer.vue'
import AppSectionHeader from '@/components/ui/AppSectionHeader.vue'

/**
 * Config
 */
import { identityConfig } from '@/config/identity'

/**
 * Utilities
 */
import { hostLabel } from '@/utils/hostLabel'

const { t } = useI18n()

/** Every title the entity holds, primary first — the order `entities.ts` emits `jobTitle` in. */
const titles = [identityConfig.role, ...identityConfig.jobTitles].filter(Boolean)

/** The parts of the location that are set. The configuration leaves most of them empty. */
const place = [identityConfig.location.city, identityConfig.location.country].filter(Boolean).join(', ')

/*
 * Both lists accept a bare string or an object, so they are normalised once here rather than with a
 * ternary at every point of use in the template. A topic that carries no `sameAs` renders as plain
 * text: linking it somewhere approximate would be worse than not linking it at all.
 */
const topics = identityConfig.topics.map(topic => (typeof topic === 'string' ? { name: topic } : topic))

const languages = identityConfig.languages.map(language => (typeof language === 'string' ? language : language.name))
</script>

<template>
  <section class="relative overflow-hidden">
    <AppBackdrop dots />

    <AppContainer class="relative py-20 md:py-24">
      <AppBadge class="enter font-mono uppercase tracking-[0.2em]" style="--enter-step: 0">
        <Sparkles class="h-3.5 w-3.5" aria-hidden="true" />
        {{ t('author.eyebrow') }}
      </AppBadge>

      <!--
        The name is the heading rather than a translated string: it is the entity's `name` in the
        graph, and a page that profiles someone under a different name than the node it points at is
        the contradiction the whole `@id` discipline exists to prevent.
      -->
      <h1 class="mt-6 font-headline text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl">
        {{ identityConfig.name }}
      </h1>

      <p v-if="titles.length" class="enter mt-4 font-headline text-lg text-primary" style="--enter-step: 1">
        {{ titles.join(' · ') }}
      </p>

      <p class="enter mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground" style="--enter-step: 2">
        {{ identityConfig.description }}
      </p>

      <ul class="enter mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm" style="--enter-step: 3">
        <li v-if="place" class="inline-flex items-center gap-2 text-muted-foreground">
          <MapPin class="h-4 w-4 text-primary" aria-hidden="true" />
          <span class="sr-only">{{ t('author.meta.based') }}:</span>
          {{ place }}
        </li>

        <li v-if="languages.length" class="inline-flex items-center gap-2 text-muted-foreground">
          <Languages class="h-4 w-4 text-primary" aria-hidden="true" />
          <span class="sr-only">{{ t('author.meta.speaks') }}:</span>
          {{ languages.join(', ') }}
        </li>
      </ul>

      <div class="enter mt-10 flex flex-wrap gap-3" style="--enter-step: 4">
        <!--
          Both actions come from the configuration: the same address the `contactPoint` publishes and
          the same URL the node's `url` carries. The `rel="me"` replaces the `noreferrer` AppButton
          puts on every external link, which is the right default for a third party and the wrong one
          here — this origin is the entity's own, and the link is the HTML half of the claim the
          JSON-LD makes with `sameAs`. Neither link opens a new tab, so `noopener` would buy nothing.
        -->
        <AppButton v-if="identityConfig.email" :href="`mailto:${identityConfig.email}`">
          <AtSign class="h-4 w-4" aria-hidden="true" />
          {{ t('author.actions.email') }}
        </AppButton>

        <AppButton v-if="identityConfig.url" variant="outline" :href="identityConfig.url" rel="me">
          <Globe class="h-4 w-4" aria-hidden="true" />
          {{ t('author.actions.website') }}
        </AppButton>
      </div>
    </AppContainer>
  </section>

  <AppContainer v-if="topics.length" as="section" aria-labelledby="topics-heading" class="py-12 md:py-16">
    <AppSectionHeader
      id="topics-heading"
      :eyebrow="t('author.topics.eyebrow')"
      :title="t('author.topics.title')"
      :description="t('author.topics.note')"
    />

    <!--
      The same array that becomes `knowsAbout`. A topic carrying a `sameAs` links to the canonical
      page for the thing, so a consumer reconciles it against something it already knows instead of
      disambiguating a bare label.
    -->
    <ul class="mt-10 flex flex-wrap gap-2">
      <li v-for="topic in topics" :key="topic.name">
        <a
          v-if="topic.sameAs"
          :href="topic.sameAs"
          target="_blank"
          rel="noreferrer"
          class="group inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-4 py-1.5 text-sm text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
        >
          <Compass class="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          {{ topic.name }}
          <ArrowUpRight
            class="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </a>

        <AppBadge v-else tone="outline">{{ topic.name }}</AppBadge>
      </li>
    </ul>
  </AppContainer>

  <AppContainer
    v-if="identityConfig.identifiers.length"
    as="section"
    aria-labelledby="identifiers-heading"
    class="py-12 md:py-16"
  >
    <AppSectionHeader
      id="identifiers-heading"
      :eyebrow="t('author.identifiers.eyebrow')"
      :title="t('author.identifiers.title')"
      :description="t('author.identifiers.note')"
    />

    <div class="mt-10 grid gap-5 md:grid-cols-2">
      <AppCard
        v-for="(identifier, index) in identityConfig.identifiers"
        :key="identifier.propertyID"
        :title="identifier.propertyID"
        :heading-level="3"
        interactive
        class="reveal"
        :style="{ '--reveal-step': index }"
      >
        <template #icon>
          <BadgeCheck class="h-5 w-5" aria-hidden="true" />
        </template>

        <p class="font-code text-sm text-foreground">{{ identifier.value }}</p>

        <template #footer>
          <a
            v-if="identifier.url"
            :href="identifier.url"
            target="_blank"
            rel="me noreferrer"
            class="group inline-flex items-center gap-1 text-sm text-muted-foreground transition hover:text-foreground"
          >
            {{ hostLabel(identifier.url) }}
            <ArrowUpRight
              class="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </a>
        </template>
      </AppCard>
    </div>
  </AppContainer>

  <AppContainer
    v-if="identityConfig.profiles.length"
    as="section"
    aria-labelledby="profiles-heading"
    class="pb-24 pt-12"
  >
    <AppSectionHeader
      id="profiles-heading"
      :eyebrow="t('author.profiles.eyebrow')"
      :title="t('author.profiles.title')"
      :description="t('author.profiles.note')"
    />

    <!--
      `rel="me"` for the same reason the footer carries it: these are the URLs the JSON-LD publishes
      as `sameAs`, and a claim asserted on one side only is unverifiable by design. `noreferrer`
      here because they are third parties, unlike the entity's own site in the hero.
    -->
    <ul class="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <li v-for="(profile, index) in identityConfig.profiles" :key="profile">
        <a
          :href="profile"
          target="_blank"
          rel="me noreferrer"
          class="reveal group flex items-center justify-between gap-4 rounded-2xl border border-border bg-card/50 px-5 py-4 text-sm text-muted-foreground backdrop-blur-sm transition hover:border-primary/40 hover:text-foreground"
          :style="{ '--reveal-step': index }"
        >
          <span class="truncate font-code">{{ hostLabel(profile) }}</span>
          <ArrowUpRight
            class="h-4 w-4 shrink-0 text-primary transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </a>
      </li>
    </ul>
  </AppContainer>
</template>
